import { CommonModule } from '@angular/common';
import { Component, effect, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';

import { AccountTypeChangeRequest, AccountTypeChangeService } from '../../services/account-type-change.service';
import { AuthService } from '../../services/auth.service';
import { ChangeAccountTypePayload, ProfileService } from '../../services/profile.service';
import { formatModerationAwareSaveError } from '../../utils/api-error.utils';

type AccountTypeOption = {
  id: number;
  icon: string;
  titleKey: string;
  descriptionKey: string;
};

@Component({
  selector: 'app-account-type-change-modal',
  standalone: true,
  imports: [TranslatePipe, CommonModule, FormsModule],
  templateUrl: './account-type-change-modal.component.html',
  styleUrls: ['./account-type-change-modal.component.scss'],
})
export class AccountTypeChangeModalComponent {
  private readonly accountTypeChange = inject(AccountTypeChangeService);
  private readonly auth = inject(AuthService);
  private readonly profileService = inject(ProfileService);
  private readonly translate = inject(TranslateService);

  readonly request = this.accountTypeChange.request;
  readonly roleOptions: AccountTypeOption[] = [
    { id: 3, icon: '🎸', titleKey: 'ACCOUNT_TYPES.GIGGER_MODAL_TITLE', descriptionKey: 'ACCOUNT_TYPES.GIGGER_MODAL_DESCRIPTION' },
    { id: 2, icon: '🏢', titleKey: 'ACCOUNT_TYPES.GIGPLACE_MODAL_TITLE', descriptionKey: 'ACCOUNT_TYPES.GIGPLACE_MODAL_DESCRIPTION' },
    { id: 1, icon: '🎶', titleKey: 'ACCOUNT_TYPES.PUBLIC', descriptionKey: 'ACCOUNT_TYPES.PUBLIC_DESCRIPTION' },
  ];

  selectedRole: number | null = null;
  acctDisplayName = '';
  acctBio = '';
  acctMemberCount: number | null = null;
  acctSocialName = '';
  acctCapacity: number | null = null;
  acctAddress = '';
  acctError = '';
  isChangingAccountType = false;

  private lastInitializedRequestId = 0;
  private readonly requestInitializer = effect(() => {
    const request = this.request();
    if (!request || request.id === this.lastInitializedRequestId) {
      return;
    }

    this.lastInitializedRequestId = request.id;
    this.populateFromRequest(request);
  });

  get isOpen(): boolean {
    return this.request() !== null;
  }

  get currentRole(): number {
    return this.request()?.currentRole ?? this.auth.currentUser()?.primary_role ?? 1;
  }

  get currentRoleLabelKey(): string {
    return this.roleOptions.find((role) => role.id === this.currentRole)?.titleKey ?? 'COMMON.UNKNOWN';
  }

  close(): void {
    if (this.isChangingAccountType) {
      return;
    }

    this.accountTypeChange.close();
  }

  selectRole(roleId: number): void {
    if (roleId === this.currentRole) {
      return;
    }

    this.selectedRole = roleId;
  }

  async confirmAccountTypeChange(): Promise<void> {
    const activeRequest = this.request();
    if (!activeRequest || !this.selectedRole || this.isChangingAccountType) {
      return;
    }

    this.acctError = '';
    this.isChangingAccountType = true;

    const payload: ChangeAccountTypePayload = {
      primaryRole: this.selectedRole,
      displayName: this.acctDisplayName.trim() || undefined,
      bio: this.acctBio.trim() || undefined,
    };

    if (this.selectedRole === 3 && this.acctMemberCount != null) {
      payload.memberCount = this.acctMemberCount;
    }

    if (this.selectedRole === 2) {
      if (this.acctSocialName.trim()) {
        payload.socialName = this.acctSocialName.trim();
      }
      if (this.acctCapacity != null) {
        payload.capacity = this.acctCapacity;
      }
      if (this.acctAddress.trim()) {
        payload.addressText = this.acctAddress.trim();
      }
    }

    try {
      await this.profileService.changeAccountType(payload);
      await this.profileService.fetchMyProfile();
      await this.auth.refreshCurrentUser();
      this.accountTypeChange.close();
      await activeRequest.onChanged?.();
    } catch (error) {
      this.acctError = formatModerationAwareSaveError(
        error,
        this.translate.instant('ACCOUNT_TYPES.CHANGE_ERROR'),
        (key, params) => this.translate.instant(key, params),
      );
    } finally {
      this.isChangingAccountType = false;
    }
  }

  private populateFromRequest(request: AccountTypeChangeRequest): void {
    const currentUser = this.auth.currentUser();
    this.selectedRole = null;
    this.acctDisplayName = request.displayName ?? currentUser?.display_name ?? '';
    this.acctBio = request.bio ?? currentUser?.bio ?? '';
    this.acctMemberCount = null;
    this.acctSocialName = '';
    this.acctCapacity = null;
    this.acctAddress = '';
    this.acctError = '';
    this.isChangingAccountType = false;
  }
}
